<template>
    <AddFileSection
        :id="AddFileFormSectionIds.SEND_WIRE_INSTRUCTIONS"
        sectionDescription="Select clients to send secure wire instructions for this file."
        sectionHeading="Send wire instructions"
    >
        <div class="mt-3 grid gap-4">
            <SelectWireInstructions
                v-model:wireDoc="addFileStore.wireDoc"
                :branchId="addFileStore.branch?.id"
                :disabled="isSelectWireDocDisabled"
                :error="wireDocError"
                @optionSelected="clearServerValidationError('wireDoc_id')"
            />
            <ClientMultiSelect
                v-if="showRecipients"
                :error="wireDocRecipientsError"
                :name="WIRE_DOC_RECIPIENTS_NAME"
                @update:modelValue="onRecipientsUpdate"
            />
        </div>
    </AddFileSection>
</template>

<script setup lang="ts">
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { WIRE_DOC_RECIPIENTS_NAME } from '@/company-portal/add-file/selectors';
import { clearServerValidationError, serverValidationErrors } from '@/company-portal/add-file/submission-error';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientMultiSelect from '@/components/Views/Company/AddFile/ClientMultiSelect.vue';
import type { DsMultiSelectClientOption } from '@/components/Views/Company/AddFile/useClientMultiSelect';
import SelectWireInstructions from '@/components/Views/Company/shared/SelectWireInstructions.vue';
import { filterWireDocsByBranch } from '@/components/Views/Company/shared/wire-instructions-utils';
import { useUserStore } from '@/stores/company/user.store';
import { computed, watchEffect } from 'vue';

withDefaults(
    defineProps<{
        showRecipients?: boolean;
    }>(),
    {
        showRecipients: true,
    },
);

const addFileStore = useAddFileStore();
const userStore = useUserStore();

const tpsWireDoc = computed(() => {
    const wireDocId = addFileStore.tpsOrder?.wire_doc_id;
    if (!wireDocId) {
        return;
    }
    return filterWireDocsByBranch(userStore.availableWireDocs, addFileStore.branch?.id).find(wireDoc => wireDoc.id === wireDocId);
});

watchEffect(() => {
    addFileStore.wireDoc = tpsWireDoc.value;
});

const isSelectWireDocDisabled = computed(
    () => !addFileStore.branch || (!!tpsWireDoc.value && !addFileStore.tpsOrder?.force_wire_doc_selection_true),
);

const onRecipientsUpdate = (clients: DsMultiSelectClientOption[]) => {
    addFileStore.wireDocRecipientClientIds = clients.map(client => client.id);
    clearServerValidationError('wireDoc_recipients');
};

const wireDocError = computed(() => serverValidationErrors.value?.wireDoc_id?.join('') ?? '');
const wireDocRecipientsError = computed(() => serverValidationErrors.value?.wireDoc_recipients?.join('') ?? '');
</script>
